import { createConsumer } from '@rails/actioncable'
import type { Consumer, Subscription } from '@rails/actioncable'
import { isShowcaseMode } from '@/lib/showcase-mode'
import { parseToken } from '@/lib/jwt-expiry'

const TOKEN_KEY = 'ledger_core_token'

function resolveCableUrl(): string {
  const apiUrl = window.__LEDGER_CONFIG__?.apiUrl || import.meta.env.VITE_LEDGER_API_URL
  if (!apiUrl) return '/cable'
  const base = apiUrl.replace(/\/$/, '')
  const wsBase = base.replace(/^http/, 'ws')
  return `${wsBase}/cable`
}

let authenticatedConsumer: Consumer | null = null

/**
 * In showcase mode return a disconnected consumer that never opens a socket.
 * We create it once and reuse - callers can safely call .subscriptions.create()
 * on it; the subscription callbacks simply never fire.
 */
let showcaseConsumer: Consumer | null = null
function getShowcaseConsumer(): Consumer {
  if (!showcaseConsumer) {
    showcaseConsumer = createConsumer('')
    showcaseConsumer.disconnect()
  }
  return showcaseConsumer
}

/** Get or create the cable consumer (no token in URL - auth is inline). */
export function getCableConsumer(): Consumer {
  if (isShowcaseMode()) return getShowcaseConsumer()
  if (!authenticatedConsumer) {
    authenticatedConsumer = createConsumer(resolveCableUrl())
  }
  return authenticatedConsumer
}

/** Disconnect and reset the consumer (call on logout). */
export function disconnectCable(): void {
  stopReconnectLoop()
  disconnectApiChannel()
  if (authenticatedConsumer) {
    authenticatedConsumer.disconnect()
    authenticatedConsumer = null
  }
}

/**
 * Reconnect the socket (for actual connection failures only).
 * After the socket re-opens, the ApiChannel `connected` callback
 * automatically re-sends the authenticate action.
 */
export function reconnectCable(): void {
  disconnectCable()
  getCableConsumer().connect()
  ensureApiSubscription()
}

/** Create an anonymous cable consumer for public pages (no JWT). */
export function createAnonymousCableConsumer(): Consumer {
  return createConsumer(resolveCableUrl())
}

// ─── ApiChannel: WebSocket RPC transport ─────────────────────────

/** Response shape from the backend ApiChannel. */
export interface WsResponse {
  request_id: string
  status: number
  headers: Record<string, string>
  body: string
}

/** Pending promise keyed by request_id. */
interface PendingRequest {
  resolve: (res: WsResponse) => void
  reject: (err: Error) => void
  timer: ReturnType<typeof setTimeout>
}

const WS_TIMEOUT_MS = 15_000
const pendingRequests = new Map<string, PendingRequest>()

let apiSubscription: Subscription | null = null
let apiChannelConnected = false

// ─── Reconnect retry loop ───────────────────────────────────────
const RECONNECT_INTERVAL_MS = 7_000      // Retry every ~7 seconds
let reconnectTimer: ReturnType<typeof setInterval> | null = null
let _cableReconnecting = false

type CableStatusListener = (reconnecting: boolean) => void
const cableStatusListeners = new Set<CableStatusListener>()

/** Whether the cable layer is actively trying to reconnect. */
export function isCableReconnecting(): boolean { return _cableReconnecting }

/** Subscribe to cable reconnecting state changes. */
export function onCableStatusChange(fn: CableStatusListener): () => void {
  cableStatusListeners.add(fn)
  return () => { cableStatusListeners.delete(fn) }
}

function setCableReconnecting(value: boolean) {
  if (_cableReconnecting === value) return
  _cableReconnecting = value
  cableStatusListeners.forEach((fn) => fn(value))
}

function startReconnectLoop(): void {
  if (reconnectTimer) return
  setCableReconnecting(true)

  reconnectTimer = setInterval(() => {
    if (apiChannelConnected) {
      stopReconnectLoop()
      return
    }
    // Stop retrying once the token has expired — fall back to offline mode
    const token = localStorage.getItem(TOKEN_KEY)
    if (!token) {
      stopReconnectLoop()
      return
    }
    const parsed = parseToken(token)
    if (parsed && parsed.exp * 1000 <= Date.now()) {
      stopReconnectLoop()
      return
    }
    // Attempt to reconnect the consumer
    const consumer = getCableConsumer()
    consumer.connect()
  }, RECONNECT_INTERVAL_MS)
}

function stopReconnectLoop(): void {
  if (reconnectTimer) {
    clearInterval(reconnectTimer)
    reconnectTimer = null
  }
  setCableReconnecting(false)
}

// ─── Auth promise: resolves when ApiChannel receives { type: "authenticated" } ──
let authResolve: (() => void) | null = null
let authPromise: Promise<void> | null = null

function resetAuthPromise(): void {
  authPromise = new Promise<void>((resolve) => { authResolve = resolve })
}

/**
 * Returns a promise that resolves once the ApiChannel has received
 * an `authenticated` confirmation from the server.  Used by hooks
 * (LedgerChannel, ImportProgress) to defer their subscriptions.
 */
export function waitForCableAuth(): Promise<void> {
  ensureApiSubscription()
  if (apiChannelConnected) return Promise.resolve()
  if (!authPromise) resetAuthPromise()
  return authPromise!
}

function ensureApiSubscription(): Subscription | null {
  if (isShowcaseMode()) return null
  if (apiSubscription) return apiSubscription

  resetAuthPromise()

  const consumer = getCableConsumer()
  apiSubscription = consumer.subscriptions.create('ApiChannel', {
    connected() {
      // Socket opened - send inline authenticate with current token
      const token = localStorage.getItem(TOKEN_KEY)
      if (token && apiSubscription) {
        apiSubscription.perform('authenticate', { token })
      }
      stopReconnectLoop()
    },
    disconnected() {
      apiChannelConnected = false
      resetAuthPromise()
      // Reject all pending requests so they fall back to HTTP
      for (const [id, pending] of pendingRequests) {
        clearTimeout(pending.timer)
        pending.reject(new Error('ws_disconnected'))
        pendingRequests.delete(id)
      }
      // Start retrying reconnection
      startReconnectLoop()
    },
    rejected() {
      apiChannelConnected = false
      resetAuthPromise()
    },
    received(data: WsResponse & { type?: string; ref?: string }) {
      // Handle auth lifecycle messages
      if (data.type === 'authenticated') {
        apiChannelConnected = true
        stopReconnectLoop()
        authResolve?.()
        return
      }
      if (data.type === 'auth_failed') {
        apiChannelConnected = false
        return
      }
      // Latency pong
      if (data.type === 'pong') {
        const sent = pendingPings.get(data.ref ?? '')
        if (sent !== undefined) {
          pendingPings.delete(data.ref!)
          pingCallback?.(Math.round(performance.now() - sent))
        }
        return
      }

      // Normal RPC response
      const pending = pendingRequests.get(data.request_id)
      if (pending) {
        clearTimeout(pending.timer)
        pendingRequests.delete(data.request_id)
        pending.resolve(data)
      }
    },
  })
  return apiSubscription
}

/** Whether the ApiChannel WebSocket is authenticated and ready. */
export function isWsReady(): boolean {
  try {
    ensureApiSubscription()
  } catch {
    // WebSocket constructor can throw synchronously for non-ws:// schemes
    return false
  }
  return apiChannelConnected && !!apiSubscription
}

/**
 * Re-authenticate the existing ApiChannel with a fresh token.
 * If the socket is disconnected, reconnect it first so the
 * authenticate action actually reaches the server.
 */
export function updateCableToken(token: string): void {
  if (!apiSubscription) return

  if (apiChannelConnected) {
    // Socket is alive — re-auth inline
    apiSubscription.perform('authenticate', { token })
  } else {
    // Socket is dead — reconnect; the `connected()` callback will
    // read the fresh token from localStorage and authenticate.
    const consumer = getCableConsumer()
    consumer.connect()
  }
}

/**
 * Send a request over the ApiChannel WebSocket.
 * Returns a Promise that resolves with the raw response or rejects on timeout/disconnect.
 */
export function wsRequest(
  requestId: string,
  method: string,
  path: string,
  body?: string | null,
): Promise<WsResponse> {
  const sub = ensureApiSubscription()
  if (!sub || !apiChannelConnected) {
    return Promise.reject(new Error('ws_not_connected'))
  }

  return new Promise<WsResponse>((resolve, reject) => {
    const timer = setTimeout(() => {
      pendingRequests.delete(requestId)
      reject(new Error('ws_timeout'))
    }, WS_TIMEOUT_MS)

    pendingRequests.set(requestId, { resolve, reject, timer })

    sub.send({
      request_id: requestId,
      method,
      path,
      body: body ?? null,
    })
  })
}

/** Tear down the ApiChannel subscription (called during disconnect). */
function disconnectApiChannel(): void {
  if (apiSubscription) {
    apiSubscription.unsubscribe()
    apiSubscription = null
  }
  apiChannelConnected = false
  resetAuthPromise()
  for (const [id, pending] of pendingRequests) {
    clearTimeout(pending.timer)
    pending.reject(new Error('ws_disconnected'))
    pendingRequests.delete(id)
  }
}

// ─── WebSocket latency ping loop ────────────────────────────────

const WS_PING_INTERVAL_MS = 10_000
const pendingPings = new Map<string, number>() // ref → performance.now()
let pingTimer: ReturnType<typeof setInterval> | null = null
let pingCallback: ((ms: number) => void) | null = null

function sendWsPing(): void {
  if (!apiSubscription || !apiChannelConnected) return
  const ref = crypto.randomUUID()
  pendingPings.set(ref, performance.now())
  apiSubscription.send({ type: 'ping', ref })
  // Clean up if no pong within 5s
  setTimeout(() => pendingPings.delete(ref), 5_000)
}

/**
 * Start a 10-second latency ping loop over the WebSocket.
 * `onPing` is called with the round-trip time in ms for each pong.
 */
export function startWsPingLoop(onPing: (ms: number) => void): void {
  stopWsPingLoop()
  pingCallback = onPing
  // Fire immediately, then every 10s
  sendWsPing()
  pingTimer = setInterval(sendWsPing, WS_PING_INTERVAL_MS)
}

/** Stop the latency ping loop. */
export function stopWsPingLoop(): void {
  if (pingTimer) {
    clearInterval(pingTimer)
    pingTimer = null
  }
  pingCallback = null
  pendingPings.clear()
}
